var React = require('react');
var { useState, useRef } = React;
var { normalizeConditionRule } = require('../lib/conditionRule');
var { remapClassifierConnections, cloneTemplateNode, compactConditionTitle, bridgeConnectionsAroundNodes, syncHyperparamDefault } = require('../lib/canvasUtils');
var {
  isLockedDuplicateNode, getDuplicateSourceNodeId, isDuplicableNode,
  buildDuplicateNodeTitle, getLockedDuplicateFields, clonePlainData,
  isClassifierLikeNode, isToolAgentNode
} = require('../lib/nodeTypes');

// Tool nodes (wired from a Tool Calling agent's bottom anchor) only exist to
// serve that agent — they're not standalone steps. So when a Tool Calling node
// is deleted, its tool nodes go with it. Given the ids already being deleted,
// return the full set including every tool node wired to any tool-agent in it.
// Pure: takes nodes + connections + the initial id set, returns an expanded set.
function expandDeletionWithAgentTools(nodes, connections, idSet) {
  var nodeById = {};
  (nodes || []).forEach(function(n) { if (n && n.id != null) nodeById[String(n.id)] = n; });
  var expanded = Object.assign({}, idSet);
  (connections || []).forEach(function(c) {
    if (!c || String(c.fromAnchor || '') !== 'bottom') return;
    var fromNode = nodeById[String(c.from)];
    // The agent being deleted owns this tool wire → delete the tool node too.
    if (expanded[String(c.from)] && isToolAgentNode(fromNode)) {
      expanded[String(c.to)] = true;
    }
  });
  return expanded;
}

function useNodeEditing(opts) {
  var setWorkflowState = opts.setWorkflowState;
  var rawNodeById = opts.rawNodeById;
  var nodeById = opts.nodeById;
  var nodeOutputByIdRef = opts.nodeOutputByIdRef;
  var setSelectedNodeIds = opts.setSelectedNodeIds;
  var setSelectedConnectionId = opts.setSelectedConnectionId;
  var selectedNodeIdsRef = opts.selectedNodeIdsRef;
  var onEditCustomNode = opts.onEditCustomNode;
  var onManualNodeAutoFix = opts.onManualNodeAutoFix;
  // When true (Live read-only view), every node is treated as locked so no edit
  // path mutates the published snapshot.
  var readOnly = !!opts.readOnly;
  var strictReadOnly = !!opts.strictReadOnly;

  var [editingAgentNodeId, setEditingAgentNodeId] = useState(null);
  var [editingClassifierNodeId, setEditingClassifierNodeId] = useState(null);
  var [pendingAgentDrop, setPendingAgentDrop] = useState(null);
  var [pendingClassifierDrop, setPendingClassifierDrop] = useState(null);
  var lastNonEmptyTitleRef = useRef({});

  function isNodeLocked(nodeId) {
    var node = rawNodeById[String(nodeId || '')];
    if (!isLockedDuplicateNode(node)) return false;
    var sourceId = getDuplicateSourceNodeId(node);
    return !!(sourceId && sourceId !== String(nodeId || '') && rawNodeById[sourceId]);
  }

  // Variable renaming helpers
  function escapeRegExp(value) {
    return String(value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  function replaceStandaloneVariableRef(textValue, oldName, newName) {
    if (typeof textValue !== 'string') return textValue;
    var oldRef = '@' + oldName;
    if (textValue === oldRef) return '@' + newName;
    if (textValue.indexOf(oldRef + '.') === 0) return '@' + newName + textValue.slice(oldRef.length);
    var pattern = new RegExp('^(\\s*)' + escapeRegExp(oldRef) + '(\\s*)$');
    var match = textValue.match(pattern);
    if (match) return match[1] + '@' + newName + match[2];
    var pathPattern = new RegExp('^(\\s*)' + escapeRegExp(oldRef) + '(\\.[^\\s]+)(\\s*)$');
    var pathMatch = textValue.match(pathPattern);
    if (pathMatch) return pathMatch[1] + '@' + newName + pathMatch[2] + pathMatch[3];
    return textValue;
  }

  function renameVariableInConditionRule(rule, oldName, newName) {
    if (!rule || typeof rule !== 'object') return rule;
    var changed = false;
    var nextConditions = Array.isArray(rule.conditions)
      ? rule.conditions.map(function(condition) {
        if (!condition || typeof condition !== 'object') return condition;
        var updated = Object.assign({}, condition);
        if (typeof updated.value === 'string') {
          var nextValue = replaceStandaloneVariableRef(updated.value, oldName, newName);
          if (nextValue !== updated.value) { updated.value = nextValue; changed = true; }
        }
        return updated;
      })
      : null;
    if (!changed) return rule;
    return Object.assign({}, rule, { conditions: nextConditions });
  }

  function renameVariableReferencesInNode(node, oldName, newName) {
    if (!node || typeof node !== 'object') return node;
    var nextNode = node;
    if (node.paramValues && typeof node.paramValues === 'object') {
      var keys = Object.keys(node.paramValues);
      var nextParamValues = node.paramValues;
      var paramValuesChanged = false;
      for (var i = 0; i < keys.length; i += 1) {
        var key = keys[i];
        var currentValue = node.paramValues[key];
        var updatedValue = currentValue;
        if (key === 'variable' && currentValue === oldName) {
          updatedValue = newName;
        } else if (typeof currentValue === 'string') {
          updatedValue = replaceStandaloneVariableRef(currentValue, oldName, newName);
        }
        if (updatedValue !== currentValue) {
          if (!paramValuesChanged) { nextParamValues = Object.assign({}, node.paramValues); paramValuesChanged = true; }
          nextParamValues[key] = updatedValue;
        }
      }
      if (paramValuesChanged) nextNode = Object.assign({}, nextNode, { paramValues: nextParamValues });
    }
    if (node.conditionRule && typeof node.conditionRule === 'object') {
      var nextRule = renameVariableInConditionRule(node.conditionRule, oldName, newName);
      if (nextRule !== node.conditionRule) nextNode = Object.assign({}, nextNode, { conditionRule: nextRule });
    }
    return nextNode;
  }

  // Node param changes
  function handleParamChange(nodeId, key, value) {
    // Live (read-only) mode allows editing ONLY Variable node values — not
    // hyperparameters or any other node's config. Block everything else.
    if (readOnly) {
      var rawNode = rawNodeById[String(nodeId || '')];
      var isVarValueEdit = !strictReadOnly && rawNode && rawNode.icon === 'variables' && key === 'value';
      if (!isVarValueEdit) return;
    }
    setWorkflowState(function(prev) {
      var targetNode = (prev.nodes || []).find(function(n) { return n.id === nodeId; });
      var nextNodes = prev.nodes.map(function(n) {
        if (n.id !== nodeId) return n;
        var nextParamValues = Object.assign({}, n.paramValues || {}, { [key]: value });
        var patch = { paramValues: nextParamValues };
        // Lockstep: mirror the value into the matching hyperparameter's default
        // so the "Edit this step" modal stays in agreement with this edit.
        var syncedHp = syncHyperparamDefault(n.hyperparameters, key, value);
        if (syncedHp !== n.hyperparameters) patch.hyperparameters = syncedHp;
        // Mirror sidebar behavior: keep set-variable titles in sync with the
        // picked variable so the canvas card label updates immediately
        // ("Set Google" instead of the static "Set Variable" type label).
        if (n.icon === 'set-variable' && key === 'variable') {
          var picked = String(value || '').trim();
          patch.title = picked ? 'Set ' + picked : 'Set Variable';
        }
        // Same idea for condition nodes — variable changes feed into the
        // compact title (e.g. "@Google = ?").
        if (n.icon === 'condition' && key === 'variable') {
          patch.title = compactConditionTitle(Object.assign({}, n, { paramValues: nextParamValues }));
        }
        return Object.assign({}, n, patch);
      });
      var nextConnections = prev.connections;
      if (key === 'labels' && targetNode && isClassifierLikeNode(targetNode)) {
        nextConnections = remapClassifierConnections(prev.connections, nodeId, (targetNode.paramValues || {}).labels, value);
      }
      return Object.assign({}, prev, { nodes: nextNodes, connections: nextConnections });
    });
  }

  function handleNodeTitleChange(nodeId, value) {
    if (readOnly) return; // titles are locked in Live mode
    if (isNodeLocked(nodeId)) return;
    setWorkflowState(function(prev) {
      var nodes = Array.isArray(prev && prev.nodes) ? prev.nodes : [];
      var targetNode = nodes.find(function(node) { return node && node.id === nodeId; });
      if (!targetNode || targetNode.icon === 'start') return prev;
      var nextTitle = String(value || '');
      var oldTitle = String(targetNode.title || '');
      // When the old title is empty, check if we have a stored last non-empty title
      // so that renaming through an empty intermediate state still works.
      var effectiveOldTitle = oldTitle;
      if (!effectiveOldTitle && lastNonEmptyTitleRef.current[nodeId]) {
        effectiveOldTitle = lastNonEmptyTitleRef.current[nodeId];
      }
      // Track the last non-empty title for this node
      if (oldTitle) {
        lastNonEmptyTitleRef.current[nodeId] = oldTitle;
      }
      var shouldRenameVariableRefs = effectiveOldTitle !== nextTitle && effectiveOldTitle.length > 0 && nextTitle.length > 0;
      // Clear stored title once a successful rename happens
      if (shouldRenameVariableRefs) {
        delete lastNonEmptyTitleRef.current[nodeId];
      }
      return Object.assign({}, prev, {
        nodes: nodes.map(function(node) {
          if (!node) return node;
          var updatedNode = node;
          if (shouldRenameVariableRefs) updatedNode = renameVariableReferencesInNode(updatedNode, effectiveOldTitle, nextTitle);
          if (node.id !== nodeId) return updatedNode;
          return Object.assign({}, updatedNode, { title: value });
        })
      });
    });
  }

  // Node deletion
  function deleteNode(nodeId, event) {
    if (event) { event.preventDefault(); event.stopPropagation(); }
    if (readOnly) return; // structure is locked in Live mode
    delete lastNonEmptyTitleRef.current[nodeId];
    setWorkflowState(function(prev) {
      var groups = Array.isArray(prev.groups) ? prev.groups : [];
      var deletedIdSet = {}; deletedIdSet[nodeId] = true;
      // Cascade: a Tool Calling node takes its tool nodes with it.
      deletedIdSet = expandDeletionWithAgentTools(prev.nodes, prev.connections, deletedIdSet);
      return Object.assign({}, prev, {
        nodes: (prev.nodes || []).filter(function(node) { return !deletedIdSet[node.id]; }),
        connections: bridgeConnectionsAroundNodes(prev.connections || [], deletedIdSet),
        groups: groups.map(function(g) {
          var filtered = g.nodeIds.filter(function(nid) { return !deletedIdSet[nid]; });
          if (filtered.length < 2) return null;
          if (filtered.length === g.nodeIds.length) return g;
          return Object.assign({}, g, { nodeIds: filtered });
        }).filter(Boolean)
      });
    });
    setEditingAgentNodeId(function(c) { return c === nodeId ? null : c; });
    setEditingClassifierNodeId(function(c) { return c === nodeId ? null : c; });
    setSelectedConnectionId(null);
  }

  function deleteSelectedNodes(event) {
    if (event) { event.preventDefault(); event.stopPropagation(); }
    if (readOnly) return; // structure is locked in Live mode
    var ids = Object.keys(selectedNodeIdsRef.current);
    if (ids.length === 0) return;
    var idSet = {};
    for (var i = 0; i < ids.length; i++) idSet[ids[i]] = true;
    setWorkflowState(function(prev) {
      var groups = Array.isArray(prev.groups) ? prev.groups : [];
      // Cascade: any selected Tool Calling node takes its tool nodes with it.
      var deletedIdSet = expandDeletionWithAgentTools(prev.nodes, prev.connections, idSet);
      return Object.assign({}, prev, {
        nodes: (prev.nodes || []).filter(function(node) { return !deletedIdSet[node.id]; }),
        connections: bridgeConnectionsAroundNodes(prev.connections || [], deletedIdSet),
        groups: groups.map(function(g) {
          var filtered = g.nodeIds.filter(function(nid) { return !deletedIdSet[nid]; });
          if (filtered.length < 2) return null;
          if (filtered.length === g.nodeIds.length) return g;
          return Object.assign({}, g, { nodeIds: filtered });
        }).filter(Boolean)
      });
    });
    setSelectedNodeIds({});
    setSelectedConnectionId(null);
  }

  // Duplicate / unlock
  function duplicateCustomNode(nodeId, event) {
    if (event) { event.preventDefault(); event.stopPropagation(); }
    if (readOnly) return; // structure is locked in Live mode
    var newNodeId = null;
    setWorkflowState(function(prev) {
      var nodes = Array.isArray(prev && prev.nodes) ? prev.nodes : [];
      var sourceNode = nodes.find(function(node) { return node && node.id === nodeId; });
      if (!sourceNode || !isDuplicableNode(sourceNode)) return prev;
      var sourceSourceId = getDuplicateSourceNodeId(sourceNode);
      var duplicationSourceId = sourceNode.duplicateLocked === true && sourceSourceId ? sourceSourceId : sourceNode.id;
      var nextId = sourceNode.id + '-copy-' + Date.now().toString(36);
      var nextTitle = buildDuplicateNodeTitle(nodes, sourceNode.title);
      var sourcePos = sourceNode.position || { left: 0, top: 0 };
      var clonedNode = Object.assign({}, sourceNode, {
        id: nextId,
        title: nextTitle,
        label: nextTitle,
        position: { left: Math.round(Number(sourcePos.left) + 36 || 36), top: Math.round(Number(sourcePos.top) + 36 || 36) },
        paramValues: Object.assign({}, sourceNode.paramValues || {}),
        size: sourceNode.size ? Object.assign({}, sourceNode.size) : null,
        stats: Array.isArray(sourceNode.stats) ? sourceNode.stats.map(function(s) { return Object.assign({}, s); }) : null,
        options: Array.isArray(sourceNode.options) ? sourceNode.options.slice() : null,
        hyperparameters: Array.isArray(sourceNode.hyperparameters) ? sourceNode.hyperparameters.map(function(p) { return Object.assign({}, p); }) : null,
        conditionRule: normalizeConditionRule(sourceNode.conditionRule),
        duplicateSourceNodeId: duplicationSourceId,
        duplicateLocked: true
      });
      newNodeId = nextId;
      return Object.assign({}, prev, { nodes: nodes.concat(clonedNode) });
    });
    if (newNodeId) {
      var sel = {}; sel[newNodeId] = true;
      setSelectedNodeIds(sel);
      setSelectedConnectionId(null);
    }
  }

  function unlockDuplicateNode(nodeId, event) {
    if (event) { event.preventDefault(); event.stopPropagation(); }
    if (!isNodeLocked(nodeId)) return;
    var effectiveNode = nodeById[nodeId];
    if (!effectiveNode) return;
    setWorkflowState(function(prev) {
      return Object.assign({}, prev, {
        nodes: (prev.nodes || []).map(function(node) {
          if (!node || node.id !== nodeId) return node;
          var nextNode = Object.assign({}, node);
          var fields = getLockedDuplicateFields();
          for (var i = 0; i < fields.length; i += 1) {
            var field = fields[i];
            if (Object.prototype.hasOwnProperty.call(effectiveNode, field)) {
              nextNode[field] = clonePlainData(effectiveNode[field]);
            }
          }
          nextNode.duplicateLocked = false;
          nextNode.duplicateSourceNodeId = null;
          return nextNode;
        })
      });
    });
  }

  // Editor open/close/save
  function openAgentEditor(nodeId, event) {
    if (event) { event.preventDefault(); event.stopPropagation(); }
    if (isNodeLocked(nodeId)) return;
    setEditingAgentNodeId(nodeId);
  }

  function closeAgentEditor() { setEditingAgentNodeId(null); }

  function saveAgentParams(nodeId, params) {
    if (readOnly) return; // agent config is locked in Live mode
    if (isNodeLocked(nodeId)) return;
    setWorkflowState(function(prev) {
      return Object.assign({}, prev, {
        nodes: (prev.nodes || []).map(function(node) {
          if (node.id !== nodeId) return node;
          return Object.assign({}, node, { paramValues: Object.assign({}, node.paramValues || {}, params) });
        })
      });
    });
    setEditingAgentNodeId(null);
  }

  function openClassifierEditor(nodeId, event) {
    if (event) { event.preventDefault(); event.stopPropagation(); }
    if (isNodeLocked(nodeId)) return;
    setEditingClassifierNodeId(nodeId);
  }

  function closeClassifierEditor() { setEditingClassifierNodeId(null); }

  function saveClassifierParams(nodeId, params) {
    if (readOnly) return; // classifier config is locked in Live mode
    if (isNodeLocked(nodeId)) return;
    setWorkflowState(function(prev) {
      var targetNode = (prev.nodes || []).find(function(n) { return n.id === nodeId; });
      var nextConnections = prev.connections;
      if (params && params.labels !== undefined && targetNode) {
        nextConnections = remapClassifierConnections(prev.connections, nodeId, (targetNode.paramValues || {}).labels, params.labels);
      }
      return Object.assign({}, prev, {
        nodes: (prev.nodes || []).map(function(node) {
          if (node.id !== nodeId) return node;
          return Object.assign({}, node, { paramValues: Object.assign({}, node.paramValues || {}, params) });
        }),
        connections: nextConnections
      });
    });
    setEditingClassifierNodeId(null);
  }

  function openCustomNodeEditor(nodeId, event) {
    if (event) { event.preventDefault(); event.stopPropagation(); }
    if (!onEditCustomNode) return;
    if (readOnly) return; // code editing is locked in Live mode
    if (isNodeLocked(nodeId)) return;
    var node = nodeById[nodeId];
    if (!node) return;
    var output = nodeOutputByIdRef.current[nodeId];
    onEditCustomNode(node, output);
  }

  function confirmAgentDrop(config) {
    if (!pendingAgentDrop) return;
    var pending = pendingAgentDrop;
    setPendingAgentDrop(null);
    var newNode = cloneTemplateNode(pending.template, pending.newId, pending.dropPosition);
    newNode.title = config.title || newNode.title;
    newNode.paramValues = Object.assign({}, newNode.paramValues || {}, {
      system: config.system || 'You are a helpful AI assistant.',
      temperature: typeof config.temperature === 'number' ? config.temperature : 0.7,
      variableInputs: Array.isArray(config.variableInputs) ? config.variableInputs : [],
      responseTemplate: Array.isArray(config.responseTemplate) ? config.responseTemplate : []
    });
    setWorkflowState(function(prev) {
      return Object.assign({}, prev, { nodes: prev.nodes.concat(newNode) });
    });
  }

  function cancelAgentDrop() { setPendingAgentDrop(null); }

  function confirmClassifierDrop(config) {
    if (!pendingClassifierDrop) return;
    var pending = pendingClassifierDrop;
    setPendingClassifierDrop(null);
    var newNode = cloneTemplateNode(pending.template, pending.newId, pending.dropPosition);
    newNode.paramValues = Object.assign({}, newNode.paramValues || {}, {
      labels: config.labels || 'positive,negative,neutral',
      instruction: config.instruction || ''
    });
    setWorkflowState(function(prev) {
      return Object.assign({}, prev, { nodes: prev.nodes.concat(newNode) });
    });
  }

  function cancelClassifierDrop() { setPendingClassifierDrop(null); }

  function handleManualFix(nodeId, userPrompt) {
    if (!onManualNodeAutoFix) return Promise.reject(new Error('Manual auto-fix is not available.'));
    if (readOnly) return Promise.reject(new Error('Code is read-only in the live version.'));
    if (isNodeLocked(nodeId)) return Promise.reject(new Error('Unlock this duplicate node before editing or fixing it.'));
    return onManualNodeAutoFix(nodeId, userPrompt);
  }

  function handleOpenEditWithOutput(nodeId, output) {
    if (!onEditCustomNode) return;
    if (readOnly) return; // code editing is locked in Live mode
    if (isNodeLocked(nodeId)) return;
    var node = nodeById[nodeId];
    if (!node) return;
    onEditCustomNode(node, output);
  }

  return {
    isNodeLocked: isNodeLocked,
    editingAgentNodeId: editingAgentNodeId,
    editingClassifierNodeId: editingClassifierNodeId,
    pendingAgentDrop: pendingAgentDrop,
    setPendingAgentDrop: setPendingAgentDrop,
    pendingClassifierDrop: pendingClassifierDrop,
    setPendingClassifierDrop: setPendingClassifierDrop,
    handleParamChange: handleParamChange,
    handleNodeTitleChange: handleNodeTitleChange,
    deleteNode: deleteNode,
    deleteSelectedNodes: deleteSelectedNodes,
    duplicateCustomNode: duplicateCustomNode,
    unlockDuplicateNode: unlockDuplicateNode,
    openAgentEditor: openAgentEditor,
    closeAgentEditor: closeAgentEditor,
    saveAgentParams: saveAgentParams,
    openClassifierEditor: openClassifierEditor,
    closeClassifierEditor: closeClassifierEditor,
    saveClassifierParams: saveClassifierParams,
    openCustomNodeEditor: openCustomNodeEditor,
    confirmAgentDrop: confirmAgentDrop,
    cancelAgentDrop: cancelAgentDrop,
    confirmClassifierDrop: confirmClassifierDrop,
    cancelClassifierDrop: cancelClassifierDrop,
    handleManualFix: handleManualFix,
    handleOpenEditWithOutput: handleOpenEditWithOutput,
    setEditingAgentNodeId: setEditingAgentNodeId,
    setEditingClassifierNodeId: setEditingClassifierNodeId
  };
}

module.exports = useNodeEditing;
